<script setup lang="ts">
import '@/assets/style/fonts.css'

import { toTypedSchema } from '@vee-validate/yup'
import {
  HaftarahMelodyType,
  PronunciationType,
  SentenceGroupMelodyType,
  SentenceType,
  TagType,
  TorahMelodyType,
  UserGrapheneType,
  UserHaftarahMelodyType,
  UserPronunciationType,
  UserSentenceGroupMelodyType,
  UserTorahMelodyType,
} from 'types/types'
import { useForm } from 'vee-validate'
import { computed, onMounted, reactive, ref, watchEffect } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import * as yup from 'yup'

import AnnotatedText from '@/components/AnnotatedText.vue'
import FontSelectPreview from '@/components/FontSelectPreview.vue'
import UserNameRow from '@/components/UserNameRow.vue'
import { useFont } from '@/composables/font'
import { watchSavedSetting } from '@/composables/useSavedSetting'
import { useThemeSelection } from '@/composables/useThemeSelection'
import { useUtil } from '@/composables/util'
import { DEFAULT_KAMATZ_KATAN_COLOR, DEFAULT_SHEWA_NA_COLOR } from '@/constants/annotationConstants'
import { THEME_LABELS, THEME_LIGHT, type ThemeName, VALID_THEMES } from '@/constants/practiceConstants'
import store from '@/store'
import { queryAliyahSentences } from '@/store/queryHelpers'
import { cloneRaw } from '@/util/util'

const logger = useLogger()
const { selectTheme } = useThemeSelection()
const { isReadOnly, isStudent, isTeacher } = useUtil(logger)

const BERESHIT_ALIYAH = 2249
const SAMPLE_SENTENCES = 5
const { MAX_FONT_SIZE, MAX_LINE_HEIGHT, MIN_FONT_SIZE, MIN_LINE_HEIGHT } = useFont()

interface ProfileData {
  sampleSentences: Array<SentenceType> | null
  userCopy: UserGrapheneType | null
}

const state = reactive<ProfileData>({
  sampleSentences: null,
  userCopy: null,
})

const selectedTheme = ref<ThemeName>(THEME_LIGHT)

const themeOptions = computed(() => VALID_THEMES.map((t) => ({ title: THEME_LABELS[t], value: t })))

const kippahReminder = ref(false)
const practiceTracking = ref(true)

const { defineField, errors, handleSubmit, meta } = useForm({
  validationSchema: toTypedSchema(
    yup.object({
      email: yup.string().email().required(),
      firstName: yup.string().trim().required(),
      font: yup.string().trim().required(),
      fontSize: yup.number().required(),
      gender: yup.string().trim().required(),
      lastName: yup.string().trim().required(),
      lineHeight: yup.number().required(),
    }),
  ),
})
const [email, emailAttr] = defineField('email')
const [firstName, firstNameAttr] = defineField('firstName')
const [font, fontAttr] = defineField('font')
const [fontSize, fontSizeAttr] = defineField('fontSize')
const [lineHeight, lineHeightAttr] = defineField('lineHeight')
const [lastName, lastNameAttr] = defineField('lastName')
const [gender, genderAttr] = defineField('gender')

const fontOptions = computed(() => {
  return [
    { title: 'default', value: '' },
    { title: 'Taamey Ashkenaz', value: 'ashkenaz' },
    { title: 'Taamey David', value: 'david' },
    { title: 'Taamey Frank', value: 'frank' },
    { title: 'Cardo', value: 'cardo' },
    { title: 'Noto Serif', value: 'noto' },
    { title: 'Shlomo', value: 'shlomo' },
  ]
})

const genderOptions = computed(() => {
  return [
    { title: 'Bar Mitzvah', value: 'bar' },
    { title: 'Bat Mitzvah', value: 'bat' },
    { title: 'B-Mitzvah', value: 'b' },
  ]
})

const userTorahMelodies = computed<Array<TorahMelodyType>>({
  get() {
    return state.userCopy!.torahMelodies.map((userTorahMelody: UserTorahMelodyType) => userTorahMelody.melody)
  },

  set(melodies: Array<TorahMelodyType>) {
    state.userCopy!.torahMelodies = melodies.map(
      (torahMelody: TorahMelodyType) => ({ melody: torahMelody, user: state.userCopy }) as UserTorahMelodyType,
    )
  },
})

const userHaftarahMelodies = computed<Array<HaftarahMelodyType>>({
  get() {
    return state.userCopy!.haftarahMelodies.map((userHaftarahMelody: UserHaftarahMelodyType) => userHaftarahMelody.melody)
  },

  set(melodies: Array<HaftarahMelodyType>) {
    state.userCopy!.haftarahMelodies = melodies.map(
      (haftarahMelody: HaftarahMelodyType) => ({ melody: haftarahMelody, user: state.userCopy }) as UserHaftarahMelodyType,
    )
  },
})

const userSentenceGroupMelodies = computed<Array<SentenceGroupMelodyType>>({
  get() {
    return state.userCopy!.sentenceGroupMelodies.map(
      (userSentenceGroupMelody: UserSentenceGroupMelodyType) => userSentenceGroupMelody.melody,
    )
  },

  set(melodies: Array<SentenceGroupMelodyType>) {
    state.userCopy!.sentenceGroupMelodies = melodies.map(
      (sentenceGroupMelody: SentenceGroupMelodyType) =>
        ({ melody: sentenceGroupMelody, user: state.userCopy }) as UserSentenceGroupMelodyType,
    )
  },
})

const userPronunciations = computed<Array<PronunciationType>>({
  get() {
    return state.userCopy!.pronunciations.map((userPronunciation: UserPronunciationType) => userPronunciation.pronunciation)
  },

  set(pronunciations: Array<PronunciationType>) {
    state.userCopy!.pronunciations = pronunciations.map(
      (pronunciation: PronunciationType) => ({ pronunciation, user: state.userCopy }) as UserPronunciationType,
    )
  },
})

const userTags = computed<Array<string>>({
  get() {
    return state.userCopy!.tags.filter((tag: TagType) => !tag.hidden as boolean).map((userTag: TagType) => userTag.name)
  },

  set(tags: Array<string>) {
    state.userCopy!.tags = tags.map((tag: string) => ({ name: tag }) as TagType)
  },
})

onMounted(async () => {
  await store.dispatch.user.fetchUser()
  await store.dispatch.style.fetchStyles()

  if (isStudent()) {
    await store.dispatch.student.fetchBarMitzvah()
    kippahReminder.value = store.getters.student.barMitzvahKippahReminder
    practiceTracking.value = store.getters.student.barMitzvahPracticeTracking

    watchSavedSetting(kippahReminder, store.dispatch.student.updateKippahReminder, () => store.getters.student.barMitzvahKippahReminder)
    watchSavedSetting(
      practiceTracking,
      store.dispatch.student.updatePracticeTracking,
      () => store.getters.student.barMitzvahPracticeTracking,
    )
  }

  queryAliyahSentences(BERESHIT_ALIYAH).then((sentences) => {
    state.sampleSentences = sentences.slice(0, SAMPLE_SENTENCES)
  })

  watchEffect(() => {
    state.userCopy = cloneRaw(store.getters.user.user)
    email.value = state.userCopy!.email
    firstName.value = state.userCopy!.firstName
    lastName.value = state.userCopy!.lastName
    font.value = state.userCopy!.properties?.font
    fontSize.value = state.userCopy!.properties?.fontSize
    lineHeight.value = state.userCopy!.properties?.lineHeight
    gender.value = state.userCopy?.properties?.gender
    selectedTheme.value = (state.userCopy?.properties?.theme as ThemeName) || THEME_LIGHT
  })

  watchSavedSetting(selectedTheme, selectTheme, () => (store.getters.user.user?.properties?.theme as ThemeName) || THEME_LIGHT)
})

const onSubmit = handleSubmit((values) => {
  const user = state.userCopy
  if (!user) {
    return
  }
  user.email = values.email
  user.firstName = values.firstName
  user.lastName = values.lastName
  if (user.properties) {
    user.properties.font = values.font
    user.properties.fontSize = values.fontSize
    user.properties.lineHeight = values.lineHeight
    user.properties.gender = values.gender
  }

  store.dispatch.user.updateUser({
    haftarahMelodyIDs: userHaftarahMelodies.value.map((m) => parseInt(m.id)),
    pronunciationIDs: userPronunciations.value.map((p) => parseInt(p.id)),
    sentenceGroupMelodyIDs: userSentenceGroupMelodies.value.map((m) => parseInt(m.id)),
    tags: userTags.value,
    torahMelodyIDs: userTorahMelodies.value.map((m) => parseInt(m.id)),
    user: { ...user },
  })
})
</script>

<template>
  <div v-if="store.getters.user.user && state.userCopy">
    <v-form @submit="onSubmit">
      <v-img v-if="false" class="align-start" height="400px" :src="require('../assets/images/profile-card-header.jpg')"></v-img>
      <user-name-row
        v-model:first-name="firstName"
        v-model:last-name="lastName"
        :errors="errors"
        :first-name-attrs="firstNameAttr"
        :last-name-attrs="lastNameAttr"
      />
      <v-row align-h="start">
        <v-col md="3">
          <v-text-field v-model="email" v-bind="emailAttr" :error-messages="errors.email" label="Email" variant="underlined" />
        </v-col>
        <v-col md="3">
          <v-btn
            class="mt-3"
            color="primary"
            @click="
              () => {
                $router.push({ name: 'change-password' })
              }
            "
            >Change password
          </v-btn>
        </v-col>
      </v-row>

      <template v-if="isTeacher()">
        <v-row>
          <v-col md="4">
            <v-select
              v-model="userTorahMelodies"
              chips
              deletable-chips
              item-title="description"
              :items="store.getters.style.torahMelodies"
              label="Melodic style (Torah)"
              multiple
              return-object
              variant="underlined"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col md="4">
            <v-select
              v-model="userHaftarahMelodies"
              chips
              deletable-chips
              item-title="description"
              :items="store.getters.style.haftarahMelodies"
              label="Melodic styles (Haftarah)"
              multiple
              return-object
              variant="underlined"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col md="4">
            <v-select
              v-model="userSentenceGroupMelodies"
              chips
              deletable-chips
              item-title="description"
              :items="store.getters.style.sentenceGroupMelodies"
              label="Other text melodic styles"
              multiple
              return-object
              variant="underlined"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col md="4">
            <v-select
              v-model="userPronunciations"
              chips
              :clearable="false"
              deletable-chips
              item-title="description"
              :items="store.getters.style.pronunciations"
              label="Pronunciation"
              multiple
              return-object
              variant="underlined"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col md="4">
            <v-combobox
              v-model="userTags"
              chips
              deletable-chips
              :items="userTags"
              label="Recording styles"
              multiple
              persistent-hint
              variant="underlined"
            >
              <template #no-data>
                <v-list-item>
                  <v-list-item-title>Enter a style and press <kbd>enter</kbd> to add</v-list-item-title>
                </v-list-item>
              </template>
            </v-combobox>
          </v-col>
        </v-row>

        <!-- Auto-color vowels: teacher picks colors for kamatz katan and shewa na -->
        <v-row>
          <v-col md="6">
            <div class="text-subtitle-2 text-medium-emphasis mb-4">Auto-color vowels</div>
            <div class="d-flex ga-6">
              <div>
                <span class="text-caption text-medium-emphasis">Kamatz katan</span>
                <compact-picker
                  :model-value="state.userCopy!.properties!.kamatzKatanColor || DEFAULT_KAMATZ_KATAN_COLOR"
                  @update:model-value="state.userCopy!.properties!.kamatzKatanColor = $event.hex"
                />
              </div>
              <div>
                <span class="text-caption text-medium-emphasis">Shewa na</span>
                <compact-picker
                  :model-value="state.userCopy!.properties!.shewaNaColor || DEFAULT_SHEWA_NA_COLOR"
                  @update:model-value="state.userCopy!.properties!.shewaNaColor = $event.hex"
                />
              </div>
            </div>
          </v-col>
        </v-row>
      </template>

      <v-row v-if="!store.getters.user.impersonator" align-h="start">
        <v-col md="3">
          <v-select
            v-if="state.userCopy"
            v-model="gender"
            v-bind="genderAttr"
            :error-messages="errors.gender"
            :items="genderOptions"
            label="Mitzvah flavor"
            variant="underlined"
          />
        </v-col>
      </v-row>

      <v-row v-if="isStudent()">
        <v-col md="3">
          <v-checkbox v-model="kippahReminder" :disabled="isReadOnly()" label="Remind me to wear a kippah" />
        </v-col>
        <v-col md="3">
          <v-checkbox v-model="practiceTracking" :disabled="isReadOnly()" label="Share my practice activity with my teacher" />
        </v-col>
      </v-row>

      <v-row>
        <v-col md="6">
          <v-select v-model="selectedTheme" :items="themeOptions" label="Color theme" variant="underlined" />
        </v-col>
      </v-row>

      <v-row v-if="state.userCopy" align-h="start">
        <v-col md="6">
          <v-select v-model="font" v-bind="fontAttr" :error-messages="errors.font" :items="fontOptions" label="Font" variant="underlined">
            <template #selection="{ item }">
              <font-select-preview :font-class="item.value" :title="item.title" />
            </template>
            <template #item="{ item, props }">
              <v-list-item v-bind="props">
                <font-select-preview :font-class="item.value" :title="item.title" />
              </v-list-item>
            </template>
          </v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="3">
          <v-slider
            v-model="fontSize"
            v-bind="fontSizeAttr"
            color="primary"
            :error-messages="errors.fontSize"
            label="Font size"
            :max="MAX_FONT_SIZE"
            :min="MIN_FONT_SIZE"
            :step="10"
            thumb-label
          />
        </v-col>
        <v-col md="3">
          <v-slider
            v-model="lineHeight"
            v-bind="lineHeightAttr"
            color="primary"
            :error-messages="errors.lineHeight"
            label="Line height"
            :max="MAX_LINE_HEIGHT"
            :min="MIN_LINE_HEIGHT"
            :step="0.05"
            thumb-label
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col md="8" text-align="center">
          <annotated-text
            v-if="state.userCopy && state.sampleSentences"
            :default-font="font"
            :font-size="fontSize"
            :line-height="lineHeight"
            name="sampleSentences"
            :sentences="state.sampleSentences as Array<SentenceType>"
            :show-sentence-numbers="true"
          />
        </v-col>
      </v-row>
      <v-btn class="px-8" color="primary" :disabled="!meta.valid || isReadOnly()" :loading="store.getters.user.updating" type="submit">{{
        isReadOnly() ? 'Update disabled: read-only account' : 'Update'
      }}</v-btn>
    </v-form>
  </div>
</template>
